import type React from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { docSource, docViewerKind } from '../../../../lib/doc-viewer';
import { rendersInline } from '../../../../lib/inline-attachment';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useAttachmentContent } from '../../../../lib/use-attachment-content';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { extensionOf, humanSize, typeLabel } from '../../../../lib/artifact-file';
import { attachmentPalette, type AttachmentPalette, type AttachmentTone } from './palette';
import { AudioAttachmentPlayer } from './AudioAttachmentPlayer';
import { AttachmentGraphic, attachmentImageUrl, attachmentAsFile } from './AttachmentGraphic';
import { AttachmentLabel } from './AttachmentLabel';
import { InlineAttachmentPreview } from './InlineAttachmentPreview';

export function SentAttachmentList({
  attachments,
  tone = 'bubble',
}: {
  attachments: MessageAttachment[];
  tone?: AttachmentTone;
}) {
  // Single attachment: inline-large layout. Multi-attachment: a wrapping row of uniform square
  // thumbnails — the same picture-first treatment a single file gets, just smaller.
  const palette = attachmentPalette(tone);

  if (attachments.length === 1) {
    const att = attachments[0];
    if (isAudioAttachment(att.mimeType)) return <AudioAttachmentPlayer attachment={att} tone={tone} />;
    return (
      <div style={styles.single}>
        <SingleAttachment att={att} palette={palette} />
      </div>
    );
  }
  return (
    <div style={styles.list}>
      {attachments.map((att, i) =>
        isAudioAttachment(att.mimeType) ? (
          <AudioAttachmentPlayer key={`${att.vfsPath || att.url}-${i}`} attachment={att} tone={tone} />
        ) : (
          <SentAttachmentTile key={`${att.vfsPath || att.url}-${i}`} att={att} palette={palette} />
        ),
      )}
    </div>
  );
}

/** Open the in-app viewer for a chip, given the attachment's resolved `content` (its URL, or the workspace
 *  file body a `vfsPath` artifact resolves to). The chip stays a real `<a href target="_blank">` when the
 *  content is a URL and we only PREVENT the default on a plain primary click — the same modifier bailout
 *  `Markdown`'s in-app links use — so cmd/ctrl/shift/alt-click and middle-click still open a tab, and a
 *  no-JS or pre-hydration click degrades to opening the raw URL.
 *
 *  A `null` kind NO LONGER falls through to the browser: every chip opens the same modal; the ones we
 *  cannot render say so and offer Download / Open in new tab, which is the same hand-off made visible and
 *  undoable. The resolved kind and content travel WITH the request — classification here has the
 *  attachment's mimeType, and the viewer's `WorkspaceFile` doesn't carry one — and the viewer is presented
 *  at the ROOT (`DocViewerHost`), so it outlives this row when an optimistic just-sent message reconciles. */
function useOpenAttachment() {
  const { logPageClickBi } = useGenericBi();
  return (att: MessageAttachment, content: string): React.MouseEventHandler<HTMLElement> =>
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      logPageClickBi({ page_name: 'chat', button_name: 'open_file', answer: att.fileName });
      e.preventDefault();
      const kind = docViewerKind(att.fileName, content || undefined, att.mimeType);
      const file = { name: att.fileName ?? '', isApp: false, content };
      openDocViewer({ file, kind, pageName: 'document_viewer' });
    };
}

/** The tilted file-type page reads right at this height in a chat row — big enough that the artwork's
 *  colour registers, small enough that the chip stays one line of text tall. */
const GRAPHIC_H = 30;

/** The multi-attachment tile: a square well the thumbnail fills, with the file-type graphic sized to sit
 *  inside it with a little air. */
const TILE = 88;
const TILE_GRAPHIC_H = 52;

function SingleAttachment({ att, palette }: { att: MessageAttachment; palette: AttachmentPalette }) {
  const { content, pending } = useAttachmentContent(att);
  const onClick = useOpenAttachment()(att, content);
  const isImage = att.mimeType.startsWith('image/');
  // The image itself, extracted — a reference-backed file's content is a FileRecord header FOLLOWED by the
  // URL, so handing `content` straight to an <img> renders the torn-page glyph.
  const src = attachmentImageUrl(att, content);

  // A file we can render shows its CONTENT here, not a chip that opens a window over the conversation:
  // reading the file is the point, and the popup broke the thread to do it. The chip survives for exactly
  // the files we cannot render (kind `null` — a `.zip`, a workbook), where it is still the honest
  // hand-off. An image already IS its own preview and keeps its tighter single-image layout, and audio
  // never reaches here (routed to the player above).
  if (rendersInline(att, content, pending)) {
    return <InlineAttachmentPreview att={att} content={content} />;
  }

  const body =
    isImage && src ? (
      <img src={src} alt={att.fileName} style={styles.singleImage} />
    ) : (
      <>
        <AttachmentGraphic att={att} content={content} height={GRAPHIC_H} />
        <AttachmentLabel att={att} content={content} />
      </>
    );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.singleLink(palette, isImage)}>
      {body}
    </AttachmentChip>
  );
}

/** One file in a multi-attachment message: a square thumbnail showing the file itself — its picture when it
 *  has one, else the shared `FileThumb` graphic (a document's first lines, or its file-type page), so a tile
 *  is never an empty box. The name sits under the tile on one line; the tile is the thing you read. */
function SentAttachmentTile({ att, palette }: { att: MessageAttachment; palette: AttachmentPalette }) {
  const { content, pending } = useAttachmentContent(att);
  const onClick = useOpenAttachment()(att, content);
  const isImage = att.mimeType.startsWith('image/');
  const src = attachmentImageUrl(att, content);
  const body = (
    <>
      <span style={styles.tileWell(palette)}>
        {isImage && src ? (
          <img src={src} alt={att.fileName} style={styles.tileImage} />
        ) : (
          <AttachmentGraphic att={att} content={content} height={TILE_GRAPHIC_H} />
        )}
      </span>
      <span style={styles.tileName}>{att.fileName}</span>
    </>
  );
  return (
    <AttachmentChip att={att} content={content} pending={pending} onClick={onClick} style={styles.tileLink(palette)}>
      {body}
    </AttachmentChip>
  );
}

/** The clickable chip, in three shapes by what we can do with the attachment:
 *   - the content resolves to a URL → a real `<a href>` so modified/middle clicks still open a tab;
 *   - anything else already resolved → a non-anchor button (an `<a href="">` would navigate to the current
 *     page and reload the whole app);
 *   - still resolving → a plain, non-interactive chip for the moment it takes.
 *
 *  A `vfsPath` artifact — a file the agent authored — carries `url: ''` and its location in `vfsPath`;
 *  `useAttachmentContent` resolves it to the workspace file the gallery shows (see `vfs-attachment.ts`).
 *
 *  ⚠️ The URL test is `docSource`, NOT `isBareHttpsUrl` on the raw content, and the button is NOT gated on
 *  `docViewerKind`. Both were bugs, and they stacked into an inert chip for every agent-authored file:
 *   - A promoted artifact's stored content is the `<!--sym-file:…-->` FileRecord header followed by the
 *     URL (harness `manage-artifacts-tool.ts` `encodeFileRecordContent`), so it is never a BARE https URL
 *     and never became an anchor — even though the header's own `rawUrl` says exactly where the file is.
 *     `docSource` is the load plan the viewer itself runs, so the chip and the viewer now agree.
 *   - A kind the viewer deliberately does not render (a spreadsheet — see `doc-viewer.ts` on the SheetJS
 *     CVEs — a `.zip`, anything unknown) then fell through to a non-interactive `<span>`. Those files are
 *     supposed to KEEP the download hand-off, and the viewer already has an honest "no preview" state with
 *     Download / Open in new tab. Opening that beats a chip that looks like a file and does nothing —
 *     the same rule `use-task-detail-modal` states for a failed resolve. */
function AttachmentChip({
  att,
  content,
  pending,
  onClick,
  style,
  children,
}: {
  att: MessageAttachment;
  content: string;
  pending: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const source = docSource(content);
  if (source?.kind === 'remote') {
    return (
      <a href={source.url} target="_blank" rel="noreferrer" title={att.fileName} style={style} onClick={onClick}>
        {children}
      </a>
    );
  }
  if (pending) {
    return (
      <span title={att.fileName} style={style}>
        {children}
      </span>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      title={att.fileName}
      style={{ ...style, cursor: 'pointer' }}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent<HTMLElement>);
        }
      }}
    >
      {children}
    </span>
  );
}

const styles = {
  single: { marginTop: 8 } satisfies React.CSSProperties,
  singleLink: (palette: AttachmentPalette, isImage: boolean): React.CSSProperties => ({
    // An image wrapper shrink-wraps its thumbnail (inline-block) — NOT a flex row: as a flex
    // item the <img> takes its main-axis width from the intrinsic width while `max-height` clamps
    // the cross axis independently, which breaks the aspect ratio and squishes the photo.
    display: isImage ? 'inline-block' : 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: isImage ? 0 : '6px 10px',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    color: palette.fg,
    fontSize: 12,
    textDecoration: 'none',
    maxWidth: 220,
    overflow: 'hidden',
  }),
  // width/height auto + both max caps → the browser scales the image to fit the box while
  // preserving its natural aspect ratio (portrait renders tall, landscape wide — never cropped).
  //
  // The WIDTH rule is the document card's (`InlineAttachmentPreview` — the bubble's width), because a photo
  // and a document card are the same object to a reader: an image rendered narrower than the card above it
  // reads as a different kind of thing. Reported on the phones, and true here for the same reason. The
  // height cap is the document preview's, so neither can outgrow the other.
  singleImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 320,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 8,
  } satisfies React.CSSProperties,
  label: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' } satisfies React.CSSProperties,
  labelName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 'var(--sym-weight-medium)',
  } satisfies React.CSSProperties,
  labelMeta: {
    fontSize: 11,
    lineHeight: '14px',
    color: 'rgba(var(--ink-rgb), 0.55)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies React.CSSProperties,
  list: { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 } satisfies React.CSSProperties,
  tileLink: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: TILE,
    color: palette.fg,
    fontSize: 11,
    textDecoration: 'none',
  }),
  tileWell: (palette: AttachmentPalette): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: TILE,
    height: TILE,
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    overflow: 'hidden',
  }),
  // Cover, not contain: at this size a letterboxed photo reads as a smaller box inside a box.
  tileImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } satisfies React.CSSProperties,
  tileName: {
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '14px',
  } satisfies React.CSSProperties,
};
